/**
 * Cart Toast Notification Handler
 * Shows a top-right notification when a product is added to cart
 * Prevents cart drawer from opening on add to cart
 * Does not show notification on quantity updates
 */

class CartToastNotification {
  constructor() {
    this.notification = document.getElementById('cart-toast-notification');
    if (!this.notification) return;

    this.lastCartCount = this.getCartCount();
    this.lastCartItemCount = this.getCartItemCount();
    this.isAddingToCart = false;
    this.isUpdatingQuantity = false;

    this.init();
    this.preventDrawerOnAddToCart();
    this.fixCartDrawerOpening();
  }

  getCartCount() {
    const cartIcon = document.getElementById('cart-icon-bubble');
    if (cartIcon) {
      const countElement = cartIcon.querySelector('.cart-count-bubble span');
      if (countElement) {
        const count = parseInt(countElement.textContent.trim(), 10);
        return isNaN(count) ? 0 : count;
      }
    }
    return 0;
  }

  getCartItemCount() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      const cartItems = cartDrawer.querySelectorAll('cart-item, [id^="CartDrawer-Item-"]');
      if (cartItems.length > 0) {
        return cartItems.length;
      }
    }
    return this.getCartCount();
  }

  init() {
    const originalFetch = window.fetch;
    const self = this;

    window.fetch = function (...args) {
      const url = args[0];
      const options = args[1] || {};

      if (typeof url === 'string' && url.includes('/cart/add') && options.method === 'POST') {
        const prevItemCount = self.getCartItemCount();
        const prevCount = self.getCartCount();

        self.isAddingToCart = true;
        self.isUpdatingQuantity = false;

        return originalFetch.apply(this, args).then((response) => {
          if (response.ok) {
            const checkCartUpdate = (attempt = 0) => {
              const maxAttempts = 5;
              const delay = 100 + attempt * 100;

              setTimeout(() => {
                const currentItemCount = self.getCartItemCount();
                const currentCount = self.getCartCount();

                if (prevCount === 0 && currentCount > 0) {
                  self.show();
                  self.lastCartItemCount = currentItemCount;
                  self.lastCartCount = currentCount;
                  setTimeout(() => {
                    self.isAddingToCart = false;
                  }, 800);
                  return;
                }

                if (currentItemCount > prevItemCount) {
                  self.show();
                  self.lastCartItemCount = currentItemCount;
                  self.lastCartCount = currentCount;
                  setTimeout(() => {
                    self.isAddingToCart = false;
                  }, 800);
                  return;
                }

                if (currentCount > prevCount && attempt < maxAttempts) {
                  checkCartUpdate(attempt + 1);
                  return;
                }

                if (currentCount > prevCount && attempt >= maxAttempts - 1) {
                  self.show();
                  self.lastCartItemCount = currentItemCount;
                  self.lastCartCount = currentCount;
                  setTimeout(() => {
                    self.isAddingToCart = false;
                  }, 800);
                  return;
                }

                if (currentCount !== prevCount) {
                  self.lastCartCount = currentCount;
                  self.lastCartItemCount = currentItemCount;
                }

                if (attempt >= maxAttempts - 1) {
                  setTimeout(() => {
                    self.isAddingToCart = false;
                  }, 800);
                }
              }, delay);
            };

            checkCartUpdate();
          } else {
            self.isAddingToCart = false;
          }
          return response;
        }).catch(() => {
          self.isAddingToCart = false;
        });
      }

      if (
        typeof url === 'string' &&
        (url.includes('/cart/change') || url.includes('/cart/update')) &&
        options.method === 'POST'
      ) {
        self.isUpdatingQuantity = true;
        self.isAddingToCart = false;

        return originalFetch.apply(this, args).then((response) => {
          if (response.ok) {
            setTimeout(() => {
              self.lastCartCount = self.getCartCount();
              self.lastCartItemCount = self.getCartItemCount();
              self.isUpdatingQuantity = false;
            }, 100);
          }
          return response;
        });
      }

      return originalFetch.apply(this, args);
    };
  }

  preventDrawerOnAddToCart() {
    const waitForCartDrawer = () => {
      const cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer) {
        setTimeout(waitForCartDrawer, 100);
        return;
      }

      const originalRenderContents = cartDrawer.renderContents;
      const originalOpen = cartDrawer.open;
      const self = this;
      let cartNeedsRefresh = false;

      if (originalRenderContents) {
        cartDrawer.renderContents = function (parsedState, dontOpen = false) {
          if (self.isAddingToCart && !self.isUpdatingQuantity) {
            cartNeedsRefresh = true;

            if (parsedState) {
              try {
                const wasAddingToCart = self.isAddingToCart;
                originalRenderContents.call(cartDrawer, parsedState, true);
                self.isAddingToCart = wasAddingToCart;
              } catch (error) {
                if (parsedState.sections) {
                  Object.keys(parsedState.sections).forEach((sectionId) => {
                    const sectionElement = document.getElementById(sectionId);
                    if (sectionElement && parsedState.sections[sectionId]) {
                      try {
                        const parser = new DOMParser();
                        const doc = parser.parseFromString(parsedState.sections[sectionId], 'text/html');
                        const newContent =
                          doc.body.innerHTML || doc.documentElement.innerHTML || parsedState.sections[sectionId];
                        sectionElement.innerHTML = newContent;
                      } catch (err) {
                        sectionElement.innerHTML = parsedState.sections[sectionId];
                      }
                    }
                  });
                }
              }
            }
            return;
          }

          return originalRenderContents.call(this, parsedState, dontOpen);
        };
      }

      if (originalOpen) {
        cartDrawer.open = function (trigger) {
          if (self.isAddingToCart && !self.isUpdatingQuantity) {
            return;
          }

          if (cartNeedsRefresh && !self.isAddingToCart) {
            cartNeedsRefresh = false;

            if (window.routes && window.routes.cart_url) {
              fetch(`${window.routes.cart_url}?sections=cart-drawer,cart-icon-bubble`)
                .then((response) => response.json())
                .then((data) => {
                  if (data && data.sections && originalRenderContents) {
                    originalRenderContents.call(cartDrawer, data, false);
                    setTimeout(() => {
                      originalOpen.call(cartDrawer, trigger);
                    }, 50);
                  } else {
                    originalOpen.call(cartDrawer, trigger);
                  }
                })
                .catch(() => {
                  originalOpen.call(cartDrawer, trigger);
                });
              return;
            }
          }

          return originalOpen.call(this, trigger);
        };
      }
    };

    waitForCartDrawer();
  }

  fixCartDrawerOpening() {
    const cartIcons = document.querySelectorAll('.header__icon--cart');
    const cartDrawer = document.querySelector('cart-drawer');

    if (cartIcons.length > 0 && cartDrawer) {
      cartIcons.forEach((cartIcon) => {
        const newCartIcon = cartIcon.cloneNode(true);
        cartIcon.parentNode.replaceChild(newCartIcon, cartIcon);

        newCartIcon.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.isAddingToCart = false;
          this.isUpdatingQuantity = false;
          if (cartDrawer && typeof cartDrawer.open === 'function') {
            cartDrawer.open(newCartIcon);
          }
        });
      });
    }
  }

  show() {
    if (!this.notification) return;
    this.notification.classList.add('show');
    setTimeout(() => {
      this.hide();
    }, 3000);
  }

  hide() {
    if (!this.notification) return;
    this.notification.classList.remove('show');
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CartToastNotification();
  });
} else {
  new CartToastNotification();
}
